import usePrevious from '@rio-cloud/rio-uikit/usePrevious';
import PropTypes from 'prop-types';
import { createContext, type ReactElement, useContext, useMemo, useState } from 'react';

interface RouteWidgetProviderProps {
    children?: ReactElement;
}

export interface RouteWidgetContextProps {
    assetSelected: string | undefined;
    setAssetSelected: (value?: string) => void;
    prevAssetSelected: string | undefined;
    setPrevAssetSelected: (value?: string) => void;
    departureDateTimeExpnd: boolean;
    setDepartureDateTimeExpnd: (value?: boolean) => void;
    restrictionsExpnd: boolean;
    setRestrictionsExpnd: (value?: boolean) => void;
    costsExpnd: boolean;
    setCostsExpnd: (value?: boolean) => void;
    stateOfChargeExpand: boolean;
    setStateOfChargeExpand: (value?: boolean) => void;
    charginOptionsExpnd: boolean;
    setCharginOptionsExpnd: (value?: boolean) => void;
    isAssetNotBooked: boolean;
    setIsAssetNotBooked: (value?: boolean) => void;
}

const RouteWidgetContext = createContext<RouteWidgetContextProps | null>(null);

export const RouteWidgetProvider = ({ children }: RouteWidgetProviderProps) => {
    const [isTourComputed, setIsTourComputed] = useState<boolean>(false);
    const [assetSelected, setAssetSelected] = useState<string | undefined>(undefined);
    const [prevAssetSelected, setPrevAssetSelected] = useState<string | undefined>(usePrevious(assetSelected));
    const [departureDateTimeExpnd, setDepartureDateTimeExpnd] = useState<boolean>(false);
    const [restrictionsExpnd, setRestrictionsExpnd] = useState<boolean>(false);
    const [costsExpnd, setCostsExpnd] = useState<boolean>(false);
    const [stateOfChargeExpand, setStateOfChargeExpand] = useState<boolean>(false);
    const [charginOptionsExpnd, setCharginOptionsExpnd] = useState<boolean>(false);
    const [isAssetNotBooked, setIsAssetNotBooked] = useState<boolean>(false);

    const context = useMemo(
        () => ({
            isTourComputed,
            setIsTourComputed,
            assetSelected,
            setAssetSelected,
            prevAssetSelected,
            setPrevAssetSelected,
            departureDateTimeExpnd,
            setDepartureDateTimeExpnd,
            restrictionsExpnd,
            setRestrictionsExpnd,
            costsExpnd,
            setCostsExpnd,
            stateOfChargeExpand,
            setStateOfChargeExpand,
            charginOptionsExpnd,
            setCharginOptionsExpnd,
            isAssetNotBooked,
            setIsAssetNotBooked,
        }),
        [
            isTourComputed,
            setIsTourComputed,
            assetSelected,
            setAssetSelected,
            prevAssetSelected,
            setPrevAssetSelected,
            departureDateTimeExpnd,
            setDepartureDateTimeExpnd,
            restrictionsExpnd,
            setRestrictionsExpnd,
            costsExpnd,
            setCostsExpnd,
            stateOfChargeExpand,
            setStateOfChargeExpand,
            charginOptionsExpnd,
            setCharginOptionsExpnd,
            isAssetNotBooked,
            setIsAssetNotBooked,
        ]
    );

    return (
        <RouteWidgetContext.Provider value={context as RouteWidgetContextProps}>{children}</RouteWidgetContext.Provider>
    );
};

RouteWidgetProvider.propTypes = {
    children: PropTypes.shape({}).isRequired,
};

export default RouteWidgetContext;

export const useRouteWidgetContext = () => {
    const context = useContext(RouteWidgetContext);
    if (!context) {
        throw new Error('RouteWidgetContext must be used within RouteWidgetProvider');
    }
    return context;
};
